import ArcgisConfig from "@arcgis/core/config";
import Point from "@arcgis/core/geometry/Point.js";
import * as locator from "@arcgis/core/rest/locator";
import AddressCandidate from "@arcgis/core/rest/support/AddressCandidate.js";
import { useEffect, useRef } from "react";
import AppSettings from "../../../config";
import { useParams } from "react-router-dom";
import { useClient } from "../../../hooks/use-client";

interface ArcgisGeocodingOptions {
  serviceUrl?: string;
  onPlaceSelected: (place: Pick<AddressCandidate, "address" | "location" | "attributes">) => void;
  options?: {
    maxSuggestions?: number;
    maxLocations?: number;
    countryCode?: string;
    categories?: string[];
    locationType?: "street" | "rooftop";
  };
}

/**
 * Custom hook for ArcGIS geocoding that mimics the interface of usePlacesWidget
 */
export const useArcgisWidget = ({
  serviceUrl = "https://geocode.arcgis.com/arcgis/rest/services/World/GeocodeServer",
  onPlaceSelected,
  options = {},
}: ArcgisGeocodingOptions) => {
  const { site } = useParams<{ site: string }>();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);
  const suggestionsListRef = useRef<any[]>([]);
  const client = useClient(site);
  const defaultCenter = client.query.data?.payload?.data?.attributes?.["default-center"];

  const setupAutocomplete = () => {
    const input = inputRef.current;
    if (!input) return;

    ArcgisConfig.apiKey = AppSettings.ARCGIS_API_KEY;

    if (!suggestionsRef.current) {
      const suggestionsDiv = document.createElement("div");
      suggestionsDiv.className = "arcgis-suggestions";
      suggestionsDiv.style.position = "absolute";
      suggestionsDiv.style.zIndex = "1000";
      suggestionsDiv.style.backgroundColor = "white";
      suggestionsDiv.style.border = "1px solid #ccc";
      suggestionsDiv.style.width = `${input.offsetWidth}px`;
      suggestionsDiv.style.maxHeight = "200px";
      suggestionsDiv.style.overflowY = "auto";
      suggestionsDiv.style.display = "none";

      input.parentNode?.insertBefore(suggestionsDiv, input.nextSibling);
      suggestionsRef.current = suggestionsDiv;
    }

    const handleInputChange = (e: Event) => {
      const inputElement = e.target as HTMLInputElement;
      const inputValue = inputElement.value;

      if (inputValue.length < 3) {
        if (suggestionsRef.current) suggestionsRef.current.style.display = "none";
        return;
      }

      const coords = defaultCenter ? [defaultCenter.lat, defaultCenter.lng] : [0, 0];
      locator
        .suggestLocations(serviceUrl, {
          text: inputValue,
          location: new Point({ latitude: coords[0], longitude: coords[1] }),
          ...(options.countryCode ? { countryCode: options.countryCode } : {}),
          ...(options.categories ? { categories: options.categories } : {}),
        })
        .then((suggestions) => {
          suggestionsListRef.current = suggestions;
          displaySuggestions(suggestions);
        });
    };

    const displaySuggestions = (suggestions: any[]) => {
      if (!suggestionsRef.current) return;
      suggestionsRef.current.innerHTML = "";
      if (suggestions.length === 0) return (suggestionsRef.current.style.display = "none");

      suggestions.forEach((suggestion, index) => {
        const item = document.createElement("div");
        item.className = "arcgis-suggestion-item";
        item.style.padding = "8px 12px";
        item.style.cursor = "pointer";
        item.style.borderBottom = index < suggestions.length - 1 ? "1px solid #eee" : "none";
        item.textContent = suggestion.text;

        item.addEventListener("mouseenter", () => (item.style.backgroundColor = "#f5f5f5"));
        item.addEventListener("mouseleave", () => (item.style.backgroundColor = "white"));

        item.addEventListener("click", async () => {
          if (inputRef.current) inputRef.current.value = suggestion.text;

          const candidates = await locator.addressToLocations(serviceUrl, {
            address: { SingleLine: suggestion.text },
            outFields: ["*"],
            magicKey: suggestion.magicKey,
            maxLocations: options.maxLocations || 1,
            ...(options.countryCode ? { countryCode: options.countryCode } : {}),
            ...(options.locationType ? { locationType: options.locationType } : {}),
            forStorage: true,
          });
          if (!candidates.length) return;
          onPlaceSelected({
            location: candidates[0].location,
            address: candidates[0].address,
            attributes: candidates[0].attributes,
          });

          suggestionsRef.current!.style.display = "none";
        });

        suggestionsRef.current?.appendChild(item);
      });

      suggestionsRef.current.style.display = "block";
      suggestionsRef.current.style.width = `${inputRef.current!.offsetWidth}px`;
      suggestionsRef.current.style.top = `${inputRef.current!.offsetTop + inputRef.current!.offsetHeight}px`;
      suggestionsRef.current.style.left = `${inputRef.current!.offsetLeft}px`;
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        e.target !== suggestionsRef.current &&
        e.target !== inputRef.current &&
        !suggestionsRef.current.contains(e.target as Node)
      ) {
        suggestionsRef.current.style.display = "none";
      }
    };

    input.addEventListener("input", handleInputChange);
    document.addEventListener("click", handleClickOutside);

    return () => {
      input.removeEventListener("input", handleInputChange);
      document.removeEventListener("click", handleClickOutside);

      if (suggestionsRef.current && suggestionsRef.current.parentNode) {
        suggestionsRef.current.parentNode.removeChild(suggestionsRef.current);
        suggestionsRef.current = null;
      }
    };
  };

  useEffect(() => {
    return setupAutocomplete();
  }, [serviceUrl, options]);

  return { ref: inputRef };
};

export default useArcgisWidget;
